'use strict';

const libPictView = require('pict-view');

/**
 * RoleClone — the #/RoleClone screen: pick a SOURCE role, choose what carries over (grants / edges), name
 * the NEW role, optionally retarget the customer (super-users, to localise a global template), then run the
 * clone with a modal confirm. The modern replacement for copy/pasting a role's grants by hand. All state
 * lives in AppData.RoleClone; behaviour in the RoleCloneProvider.
 */
class RoleClone extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.RoleCloneProvider;
	}

	onBeforeRender(pRenderable)
	{
		if (this.provider) { this.provider.deriveRenderState(); }
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		const tmpProvider = this.provider;
		// (Re)mount the searchable source-role picker into its fresh mount each render (the whole wrap repaints on
		// every action). Replaces the old capped `Roles/…/0/1000` preload + <select>, which hid roles past the cap.
		if (tmpProvider) { tmpProvider.mountSourcePicker(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_repaint() { this.render(); }

	_toast(pMessage, pType)
	{
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: pType || 'success' }); }
	}

	selectSource(pValue) { this.provider.selectSourceAsync(pValue).then(() => this._repaint()); }
	setField(pField, pValue) { this.provider.setField(pField, pValue); this._repaint(); }
	toggleInclude(pField) { this.provider.toggleInclude(pField); this._repaint(); }

	back() { window.location.hash = '#/Access'; }

	apply()
	{
		const tmpProvider = this.provider;
		const tmpState = tmpProvider.state;
		if (!tmpState.SourceRole || !String(tmpState.NewName || '').trim())
		{
			tmpState.Message = 'Choose a source role and enter a name for the new role.';
			this._repaint();
			return;
		}
		const tmpParts = [];
		if (tmpState.IncludeGrants) { tmpParts.push('its permission grants'); }
		if (tmpState.IncludeEdges) { tmpParts.push('its included roles/groups'); }
		const tmpWhat = tmpParts.length ? tmpParts.join(' and ') : 'no grants or edges (an empty role)';
		const tmpMessage = `Create a new role “${String(tmpState.NewName).trim()}” copying ${tmpWhat} from “${tmpState.SourceRole.Name}”? The new role has no members until you assign it.`;
		const fApply = () => tmpProvider.cloneAsync().then((pResult) =>
		{
			this._repaint();
			if (pResult) { this._toast(`Cloned — ${pResult.Succeeded} created, ${pResult.Failed} failed`, pResult.Failed ? 'error' : 'success'); }
		});
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm(tmpMessage, { title: 'Clone role', confirmLabel: 'Clone role', cancelLabel: 'Cancel' })
				.then((pOk) => { if (pOk) { fApply(); } });
		}
		else
		{
			fApply();
		}
	}
}

module.exports = RoleClone;

module.exports.default_configuration =
{
	ViewIdentifier: 'RoleClone',
	DefaultRenderable: 'RoleCloneWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.rc-wrap { padding: 1.25rem 1.5rem 2rem; max-width: 60rem; }
		.rc-head { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; margin:0 0 1rem; }
		.rc-title { font-size:1.5rem; font-weight:700; margin:0; color:var(--theme-color-text-primary,#1f2733); }
		.rc-subtitle { font-size:0.9rem; color:var(--theme-color-text-muted,#6b7686); margin:0.2rem 0 0; }
		.rc-grid { display:grid; grid-template-columns:1fr 1fr; gap:1rem; }
		@media (max-width: 760px) { .rc-grid { grid-template-columns:1fr; } }
		.rc-card { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:0.9rem 1.1rem; }
		.rc-card-head { font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-brand-primary,#2e7d74); margin:0 0 0.7rem; }
		.rc-field { margin:0 0 0.6rem; }
		.rc-label { display:block; font-size:0.68rem; font-weight:600; text-transform:uppercase; letter-spacing:0.04em; color:var(--theme-color-text-muted,#6b7686); margin:0 0 0.15rem; }
		.rc-input, .rc-select { width:100%; font-size:0.9rem; padding:0.3rem 0.45rem; border-radius:6px; border:1px solid var(--theme-color-border-default,#d7dce3); box-sizing:border-box; }
		.rc-source-meta { font-size:0.82rem; color:var(--theme-color-text-secondary,#45505f); margin:0.35rem 0 0; }
		.rc-source-count { font-size:0.82rem; color:var(--theme-color-text-muted,#6b7686); margin:0.15rem 0 0; }
		.rc-check { display:flex; align-items:center; gap:0.5rem; font-size:0.88rem; color:var(--theme-color-text-secondary,#45505f); margin:0 0 0.4rem; }
		.rc-hint { font-size:0.75rem; color:var(--theme-color-text-muted,#8894a4); margin:0.1rem 0 0.6rem 1.6rem; }
		.rc-applybar { margin:1.1rem 0 0; display:flex; align-items:center; gap:1rem; }
		.rc-note { font-size:0.82rem; color:var(--theme-color-text-muted,#6b7686); }
		.rc-result { border:1px solid var(--theme-color-border-light,#e8ebf0); border-radius:8px; background:var(--theme-color-background-tertiary,#f4f6f9); padding:0.9rem 1.1rem; margin:1rem 0 0; }
		.rc-result-title { font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-brand-primary,#2e7d74); margin:0 0 0.4rem; }
		.rc-result-list { }
		.rc-result-row { font-size:0.85rem; padding:0.1rem 0; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
	`,
	Templates:
	[
		{
			Hash: 'RoleClone-Wrap',
			Template: /*html*/`
				<div class="rc-wrap">
					<div class="rc-head">
						<div>
							<h1 class="rc-title">Clone a role</h1>
							<p class="rc-subtitle">Copy an existing role or permission group into a new, independent one — its permission grants and the roles it includes carry over, then you rename it and diverge freely. The new role has no members until you assign it.</p>
						</div>
						<button class="button is-light" onclick="_Pict.views.RoleClone.back()"><span class="icon">{~I:ChevronLeft~}</span><span>Back to Access</span></button>
					</div>
					{~TS:RoleClone-NoPerm:AppData.RoleClone.NoPermSlot~}
					{~TS:RoleClone-Message:AppData.RoleClone.MessageSlot~}
					<div class="rc-grid">
						<div class="rc-card">
							<div class="rc-card-head">1 · Source role</div>
							<div class="rc-field">
								<span class="rc-label">Role or permission group</span>
								<div id="RoleClone-SourcePicker" class="rc-picker"></div>
							</div>
							{~TS:RoleClone-SourceMeta:AppData.RoleClone.PreviewSlot~}
						</div>
						<div class="rc-card">
							<div class="rc-card-head">2 · New role</div>
							<div class="rc-field">
								<span class="rc-label">Name</span>
								<input class="rc-input" value="{~D:AppData.RoleClone.NewNameValue~}" onchange="_Pict.views.RoleClone.setField('NewName', this.value)" placeholder="e.g. Railroad Estimates Approval">
							</div>
							<div class="rc-field">
								<span class="rc-label">Include</span>
								<label class="rc-check"><input type="checkbox" {~D:AppData.RoleClone.IncludeGrantsAttr~} onchange="_Pict.views.RoleClone.toggleInclude('IncludeGrants')"> Permission grants</label>
								<label class="rc-check"><input type="checkbox" {~D:AppData.RoleClone.IncludeEdgesAttr~} onchange="_Pict.views.RoleClone.toggleInclude('IncludeEdges')"> Included roles / groups (edges)</label>
							</div>
							<div class="rc-field">
								<span class="rc-label">Target customer (0 = global)</span>
								<input class="rc-input" value="{~D:AppData.RoleClone.TargetCustomerValue~}" {~D:AppData.RoleClone.TargetCustomerReadOnly~} onchange="_Pict.views.RoleClone.setField('TargetIDCustomer', this.value)">
							</div>
						</div>
					</div>
					<div class="rc-applybar">
						<button class="button is-link" {~D:AppData.RoleClone.ApplyDisabledAttr~} onclick="_Pict.views.RoleClone.apply()"><span class="icon">{~I:Copy~}</span><span>Clone role</span></button>
						<span class="rc-note">Project-type scoping isn't part of the permission model yet — scope the new role by assigning it where needed.</span>
					</div>
					{~TS:RoleClone-Loading:AppData.RoleClone.LoadingSlot~}
					{~TS:RoleClone-Result:AppData.RoleClone.ResultSlot~}
				</div>`
		},
		{
			Hash: 'RoleClone-RoleOption',
			Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>`
		},
		{
			Hash: 'RoleClone-SourceMeta',
			Template: /*html*/`<p class="rc-source-count">Will copy: {~D:Record.Text~}</p>`
		},
		{
			Hash: 'RoleClone-NoPerm',
			Template: /*html*/`<div class="notification is-warning is-soft py-2 px-3 mb-3">You need role-manager permission to clone roles. You can review the source here, but the clone will be rejected.</div>`
		},
		{
			Hash: 'RoleClone-Message',
			Template: /*html*/`<div class="notification is-warning is-soft py-2 px-3 mb-3">{~D:Record.Message~}</div>`
		},
		{
			Hash: 'RoleClone-Loading',
			Template: /*html*/`<p class="rc-note mt-3">Cloning…</p>`
		},
		{
			Hash: 'RoleClone-Result',
			Template: /*html*/`
				<div class="rc-result">
					<div class="rc-result-title">Result — new role “{~D:Record.NewName~}” (#{~D:Record.NewIDRole~}) · {~D:Record.Succeeded~} created, {~D:Record.Failed~} failed</div>
					<div class="rc-result-list">{~TS:RoleClone-ResultRow:AppData.RoleClone.ResultRows~}</div>
				</div>`
		},
		{
			Hash: 'RoleClone-ResultRow',
			Template: /*html*/`<div class="rc-result-row"><span class="{~D:Record.StatusClass~}">{~D:Record.Label~}</span> — {~D:Record.StatusText~}</div>`
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'RoleCloneWrap',
			TemplateHash: 'RoleClone-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container',
		}
	]
};
